import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../core/sdk';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { PickList } from 'primereact/picklist';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import 'react-image-crop/dist/ReactCrop.css';

const ArchiveConfirmationDialog = observer(
    ({
        store,
        onConfirm,
        onCancel,
    }: {
        store: any;
        onConfirm: () => void;
        onCancel: () => void;
    }) => {
        const [confirmationText, setConfirmationText] = useState('');
        const searchWord: string = store.archived ? 'reaktivieren' : 'archivieren';
        const buttonText: string = store.archived ? 'Reaktivieren' : 'Archivieren';
        const headerText: string = store.archived
            ? 'Reaktivierung bestätigen'
            : 'Archivierung bestätigen';

        const handleConfirm = () => {
            if (confirmationText.toLowerCase() === searchWord) {
                onConfirm();
            }
        };

        return (
            <Dialog
                visible={store.showArchiveConfirmation}
                modal
                header={headerText}
                footer={() => (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={onCancel}
                        />
                        <Button
                            label={buttonText}
                            icon="pi pi-check"
                            className="p-button-danger"
                            onClick={handleConfirm}
                            disabled={confirmationText.toLowerCase() !== searchWord}
                        />
                    </div>
                )}
                onHide={onCancel}
            >
                <p>
                    Um das Objekt zu {searchWord}, geben Sie bitte das Wort{' '}
                    <strong>{store.archived ? '"reaktivieren"' : '"archivieren"'}</strong> in das
                    Feld unten ein.
                </p>
                <InputText
                    value={confirmationText}
                    onChange={e => setConfirmationText(e.target.value)}
                    placeholder={searchWord}
                    style={{ width: '100%' }}
                />
            </Dialog>
        );
    }
);

const itemTemplate = (item: any) => {
    const isDisabled: boolean = item.global || item.disabled || false; // Disable if global or explicitly disabled
    return (
        <div
            className={`flex flex-wrap p-2 align-items-center gap-3 ${isDisabled ? 'disabled-item' : ''}`}
            style={{
                opacity: isDisabled ? 0.8 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
                position: 'relative', // For positioning the hint
            }}
        >
            <div className="flex-1 flex flex-column gap-2">
                <span style={{ textAlign: 'left' }}>{item.name}</span>
                {item.global && (
                    <small style={{ color: '#ff6f61', fontStyle: 'italic' }}>
                        Standarmäßig ausgewählt
                    </small>
                )}
            </div>
        </div>
    );
};

const ObjectTabber = observer(({ store }: { store: any }) => {
    const handleProductChange = (event: any) => {
        // Check if any item in the source list has global === true
        const hasGlobalItem = event.source.some((item: any) => item.global);

        if (hasGlobalItem) {
            store.updateProductsAssignment(event.source, event.target);
            return; // Stop further logic
        }

        // Proceed with the normal logic if no global items are in the source
        store.updateProductsAssignment(event.source, event.target);
    };

    const handleEmployeeChange = (event: any) => {
        store.updateEmployeeAssignment(event.source, event.target);
    };

    return (
        <TabView
            activeIndex={store.activeTab}
            onTabChange={e => {
                store.onChangeTab(e.index);
            }}
        >
            <TabPanel header="Reinigungskräfte">
                <PickList
                    dataKey="id"
                    filter
                    filterBy="name"
                    source={store.unassignedEmployeeArray}
                    target={store.assignedEmployeeArray}
                    showTargetControls={false}
                    showSourceControls={false}
                    itemTemplate={itemTemplate}
                    onChange={handleEmployeeChange}
                    sourceHeader="Verfügbare Reinigungskräfte"
                    targetHeader="Zugewiesene Reinigungskräfte"
                    sourceStyle={{ height: '100%', minHeight: '20rem' }}
                    targetStyle={{ height: '100%', minHeight: '20rem' }}
                />
            </TabPanel>
            <TabPanel header="Produkte">
                <PickList
                    dataKey="id"
                    filter
                    filterBy="name"
                    source={store.unassignedProductsArray}
                    target={store.assignedProductsArray}
                    showTargetControls={false}
                    showSourceControls={false}
                    itemTemplate={itemTemplate}
                    onChange={handleProductChange}
                    sourceHeader="Verfügbare Produkte"
                    targetHeader="Zugewiesene Produkte"
                    sourceStyle={{ height: '100%', minHeight: '20rem' }}
                    targetStyle={{ height: '100%', minHeight: '20rem' }}
                />
            </TabPanel>
        </TabView>
    );
});

const ObjectManagerDropdown = observer(({ store }: { store: any }) => {
    const handleManagerChange = (e: { value: number }) => {
        store.selectManager(e.value);
    };

    const handleDropdownFocus = async () => {
        await store.fetchAvailableManagers(); // Fetch managers only if not already loaded
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label
                htmlFor="manager"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Objektleiter
            </label>
            <Dropdown
                id="manager"
                value={store.selected_manager_id}
                options={store.available_managers.map((manager: any) => ({
                    label: manager.name,
                    value: manager.id,
                }))}
                onChange={handleManagerChange}
                onFocus={handleDropdownFocus}
                placeholder="Wähle einen Objektleiter aus"
                style={{ width: '100%' }}
            />
        </div>
    );
});

const ObjectTitle = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 80) {
            store.objectname = e.target.value;
        }
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="objectname"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Objekt-Name
            </label>
            <InputText
                id="objectname"
                placeholder="Objektname"
                value={store.objectname}
                onChange={handleInputChange}
                style={{ width: '100%' }}
                data-pr-tooltip="Der Objektname muss mindestens 3 Zeichen lang sein."
                data-pr-position="top"
                data-pr-disabled={store.validObjectname}
                invalid={!store.validObjectname}
            />
            <Tooltip target="#objectname" />
        </div>
    );
});

const ObjectId = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.objectid = e.target.value;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="objectid"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Objekt-Id
            </label>
            <InputText
                id="objectid"
                placeholder="Object-Id"
                keyfilter="pint"
                disabled={store.isRepresentation}
                value={store.objectid}
                onChange={handleInputChange}
                style={{ width: '100%' }}
                data-pr-tooltip={store.validObjectIdTooltip}
                data-pr-position="top"
                data-pr-disabled={store.validObjectId}
                invalid={!store.validObjectId}
            />
            <Tooltip target="#objectid" />
        </div>
    );
});

const ObjectNote = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        store.objectnote = e.target.value;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="objectnote"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Objektbeschreibung
            </label>
            <InputTextarea
                id="objectnote"
                placeholder="Notiz"
                value={store.objectnote}
                onChange={handleInputChange}
                rows={5}
                cols={30}
                style={{ width: '100%' }}
            />
        </div>
    );
});

const ObjectAdress = observer(({ store }: { store: any }) => {
    const handleInputChangeStreet = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.street = e.target.value;
    };

    const handleInputChangeHouseNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.housenumber = e.target.value;
    };

    const handleInputChangeZipcode = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.zipcode = e.target.value;
    };

    const handleInputChangeCity = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.city = e.target.value;
    };

    if (store.isRepresentation) {
        return null; // Don't render the address fields if it's a representation
    }

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 2 }}>
                    <label
                        htmlFor="street"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Straße
                    </label>
                    <InputText
                        id="street"
                        placeholder="Straße"
                        value={store.street}
                        onChange={handleInputChangeStreet}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label
                        htmlFor="housenumber"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Hausnummer
                    </label>
                    <InputText
                        id="housenumber"
                        placeholder="Hausnummer"
                        value={store.housenumber}
                        onChange={handleInputChangeHouseNumber}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                    <label
                        htmlFor="zipcode"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Postleitzahl
                    </label>
                    <InputText
                        id="zipcode"
                        placeholder="Postleitzahl"
                        value={store.zipcode}
                        onChange={handleInputChangeZipcode}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ flex: 2 }}>
                    <label
                        htmlFor="city"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Ort
                    </label>
                    <InputText
                        id="city"
                        placeholder="Ort"
                        value={store.city}
                        onChange={handleInputChangeCity}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
        </div>
    );
});

const ObjectDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog;

    const handleObjectSave = async () => {
        try {
            await store.onSaveObject();
            // Erfolgreich => Schließe nun die Ansicht
            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Objekt erfolgreich gespeichert');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };

    const handleArchiveToggle = async () => {
        store.setShowArchiveConfirmation(true); // Öffne den Bestätigungsdialog
    };

    const confirmArchive = async () => {
        try {
            const newArchivedStatus = !store.archived; // Toggle the archived status
            await store.archiveObject(newArchivedStatus); // Call the store method to archive/unarchive
            sdk.showSuccessToast(
                newArchivedStatus
                    ? 'Objekt erfolgreich archiviert'
                    : 'Objekt erfolgreich reaktiviert'
            );
            sdk.closeDialog({
                event: 'SAVED',
            });
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Fehler beim Archivieren des Objekts');
        } finally {
            store.setShowArchiveConfirmation(false); // Schließe den Bestätigungsdialog
        }
    };

    const cancelArchive = () => {
        store.setShowArchiveConfirmation(false); // Schließe den Bestätigungsdialog
    };

    const handleObjectClone = () => {
        store.clone(); // Führe die Klon-Logik aus
        sdk.showSuccessToast('Neue Objektvorlage erstellt');
    };

    const notSaveable = store.isLoading || store.isInvalid;

    return (
        <>
            <Dialog
                visible={!!sdk.currentDialog}
                modal
                header={store?.header || ''}
                footer={() => (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: store.isCloneable ? 'space-between' : 'flex-end',
                        }}
                    >
                        {store.isCloneable && (
                            <Button
                                label="Klonen"
                                icon="pi pi-copy"
                                className="p-button-secondary"
                                onClick={handleObjectClone}
                            />
                        )}
                        <Button
                            label={store.saveButton}
                            icon="pi pi-check"
                            disabled={notSaveable}
                            onClick={handleObjectSave}
                        />
                    </div>
                )}
                style={{ width: '80rem', height: '50rem' }}
                onHide={() => sdk.closeDialog()}
            >
                {store.isLoading ? (
                    <div
                        className="flex justify-content-center align-items-center"
                        style={{ height: '100%' }}
                    >
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="dialog-content">
                        <div className={'left-column'}>
                            <div className={'card'}>
                                <ObjectTitle store={store} />
                                <ObjectId store={store} />
                                <ObjectAdress store={store} />
                                <ObjectManagerDropdown store={store} />
                                <ObjectNote store={store} />
                                {store.isArchivable && (
                                    <Button
                                        label={store.archived ? 'Reaktivieren' : 'Archivieren'}
                                        icon={store.archived ? 'pi pi-undo' : 'pi pi-archive'}
                                        className="p-button-warning"
                                        onClick={handleArchiveToggle}
                                        style={{ marginTop: '1rem', width: '100%' }}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={'right-column'}>
                            <div className={'card'}>
                                <ObjectTabber store={store} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
            <ArchiveConfirmationDialog
                store={store}
                onConfirm={confirmArchive}
                onCancel={cancelArchive}
            />
        </>
    );
});

export default ObjectDialog;
